import React, { useState } from 'react';
import {
  VStack,
  HStack,
  Input,
  Button,
  Avatar,
  FormControl,
  FormLabel,
  useColorModeValue,
  Box,
  Text,
  useToast
} from '@chakra-ui/react';
import { PaperPlaneTilt } from '@phosphor-icons/react';
import CommentImageUpload from '../CommentImageUpload';
import { validateCommentText, commentRateLimiter } from '../../utils/inputSanitization';
import { useAuth } from '../../hooks/useAuth';

const CommentInput = ({ user, onSubmit, siteId }) => {
  const [commentText, setCommentText] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user: authUser } = useAuth();
  const toast = useToast();

  const borderColor = useColorModeValue('ancient.stone', 'whiteAlpha.300');
  const labelColor = useColorModeValue('ancient.stone', 'whiteAlpha.900');

  const handleSubmit = async () => {
    if (isSubmitting) return;

    // Rate limiting check
    const rateLimitKey = `comment_${authUser?.uid || 'anonymous'}`;
    if (!commentRateLimiter.isAllowed(rateLimitKey, 5, 60000)) { // 5 comments per minute
      toast({
        title: 'Rate limit exceeded',
        description: 'Please wait before posting another comment',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    // Validate comment text
    const validation = validateCommentText(commentText);
    if (!validation.isValid) {
      toast({
        title: 'Invalid comment',
        description: validation.error,
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const success = await onSubmit(validation.sanitized, photoUrl);
      if (success) {
        setCommentText('');
        setPhotoUrl('');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      variant="parchment"
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      p={4}
      position="relative"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bg: 'ancient.parchment',
        opacity: 0.1,
        borderRadius: 'md',
        zIndex: -1
      }}
    >
      <FormControl>
        <FormLabel>
          <Text
            textStyle="hieroglyph"
            color={labelColor}
            fontSize="sm"
            mb={2}
          >
            Share Your Thoughts
          </Text>
        </FormLabel>
        <HStack align="start" spacing={{ base: 2, md: 3 }}>
          <Avatar 
            size={{ base: "sm", md: "md" }} 
            name={user?.displayName || 'Guest'} 
            bg="ancient.terracotta"
            color="white"
          />
          <VStack align="start" width="full" spacing={3}>
            <Input
              placeholder="Your comment here..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              width="full"
              variant="carved"
              size={{ base: "sm", md: "md" }}
              _placeholder={{ color: 'ancient.stone', opacity: 0.7 }}
            />
            <CommentImageUpload 
              onUploadComplete={setPhotoUrl}
              siteId={siteId}
              initialUrl={photoUrl}
            />
          </VStack>
          <Button 
            variant="relief"
            onClick={handleSubmit}
            size={{ base: "sm", md: "md" }}
            rightIcon={<PaperPlaneTilt weight="bold" />}
            aria-label="Post comment"
            isLoading={isSubmitting}
            isDisabled={!commentText.trim() && !photoUrl}
          >
            Post
          </Button>
        </HStack>
      </FormControl>
    </Box>
  );
};

export default CommentInput;
